import type { TranscriptSegment } from '../../../shared/api-types.js';
import { formatTimestamp } from '../../ui/format.js';

// Searching what was said in a meeting (owner request of 2026-10-01: "On meeting transcript page add a search
// feature to search through what was said"). The whole transcript is already in the page, so the search runs
// here and costs no request; what was searched for is kept in the address (transcript_q), so it goes to the server
// with the page when the page is reloaded or the link is opened, and the request log records the path only. A
// segment matches when every word typed appears in its text — anywhere, as part of a longer word too, as the
// browser's own find does — ignoring case and accents; a phrase in quotes must appear as written. The speaker's name
// is not searched: the search is of what was said, and a name typed is found where somebody said it.

/** The transcript tab's search in the address, namespaced beside the embedded to-do table's `todos_` keys (06-modules §5.5). */
export const TRANSCRIPT_QUERY_PARAM = 'transcript_q';

/** Why a cap: the address is untrusted input, and nobody searches a transcript with a paragraph. In code points. */
export const TRANSCRIPT_QUERY_MAX = 200;

/** Runs of white space collapsed to one space, trimmed, and cut to {@link TRANSCRIPT_QUERY_MAX} code points. */
export function normaliseTranscriptQuery(raw: string): string {
  return Array.from(raw.replace(/\s+/gu, ' ').trim()).slice(0, TRANSCRIPT_QUERY_MAX).join('').trim();
}

export function readTranscriptQuery(params: URLSearchParams): string {
  return normaliseTranscriptQuery(params.get(TRANSCRIPT_QUERY_PARAM) ?? '');
}

/** The address with the transcript search taken out; the rest (the tab, the to-do table's keys) is kept. */
export function searchWithoutTranscriptQuery(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(TRANSCRIPT_QUERY_PARAM);
  const rest = params.toString();
  return rest === '' ? '' : `?${rest}`;
}

// --- Folding -------------------------------------------------------------------------------------

/**
 * Text folded for comparison, with where each of its UTF-16 units came from in the original: unit `k` of `text`
 * stands for the original's `[start[k], end[k])`. That map is what lets a match found in the folded text be
 * marked in the words as Fellow sent them.
 */
export interface FoldedText {
  text: string;
  start: number[];
  end: number[];
}

/**
 * Quotation marks and apostrophes typed as the plain ones: a transcript's ’ and a keyboard's ' are the same
 * apostrophe, and a phrase may be quoted „like this“, « like this » or “like this”. The acute accent and the
 * backtick stand in for an apostrophe on many keyboards ("don´t").
 */
const QUOTES: Readonly<Record<string, string>> = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '‹': "'",
  '›': "'",
  'ʼ': "'",
  '′': "'",
  '´': "'",
  '`': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '‟': '"',
  '«': '"',
  '»': '"',
  '″': '"',
  '〝': '"',
  '〞': '"',
};

/**
 * Letters read as their plain letter that no Unicode decomposition reaches (ł is not l + a mark): without them an
 * unaccented "lukasz" never finds "Łukasz". Applied after lower-casing; ß is here as well because a capital ẞ only
 * lower-cases to ß, where a small one upper-cases to SS.
 */
const PLAIN_LETTERS: Readonly<Record<string, string>> = {
  'ß': 'ss',
  'ł': 'l',
  'ŀ': 'l',
  'ø': 'o',
  'đ': 'd',
  'ħ': 'h',
  'ŧ': 't',
  'œ': 'oe',
  'æ': 'ae',
};

/**
 * The marks dropped as accents — the combining diacritics of the Latin, Greek and Cyrillic scripts and the
 * optional vowel points of Hebrew and Arabic. Why not every nonspacing mark: in Devanagari, Thai and the other
 * Brahmic scripts those are vowels, and in kana the voicing marks make another syllable; dropping them would
 * make different words the same ("मेरा", my, would be found for "मरा", died).
 */
const ACCENT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0300, 0x036f],
  [0x1ab0, 0x1aff],
  [0x1dc0, 0x1dff],
  [0x20d0, 0x20ff],
  [0xfe20, 0xfe2f],
  [0x0591, 0x05bd],
  [0x05bf, 0x05bf],
  [0x05c1, 0x05c2],
  [0x05c4, 0x05c5],
  [0x05c7, 0x05c7],
  [0x0610, 0x061a],
  [0x064b, 0x065f],
  [0x0670, 0x0670],
  [0x06d6, 0x06dc],
  [0x06df, 0x06e4],
  [0x06e7, 0x06e8],
  [0x06ea, 0x06ed],
];
const WHITE_SPACE = /\s/u;

function isAccent(codePoint: number): boolean {
  return ACCENT_RANGES.some(([low, high]) => codePoint >= low && codePoint <= high);
}

/**
 * One character with the accents that follow it, folded: case folded (Σ, σ and ς alike; ß as ss), compatibility-
 * decomposed (ﬁ → fi), its accents dropped (é → e), recomposed (Hangul syllables and kana whole again), with
 * {@link QUOTES} and {@link PLAIN_LETTERS} applied. May be empty — a spacing accent on its own (¨) folds to nothing.
 */
function foldCluster(cluster: string): string {
  const quote = QUOTES[cluster];
  if (quote !== undefined) return quote;
  let folded = '';
  for (const character of cluster.toUpperCase().toLowerCase().normalize('NFKD')) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (isAccent(codePoint) || WHITE_SPACE.test(character)) continue;
    folded += PLAIN_LETTERS[character] ?? QUOTES[character] ?? character;
  }
  return folded.normalize('NFC');
}

/**
 * Folds `text` for searching (see {@link foldCluster}), a character together with the accents typed after it, so a
 * separate accent belongs to its letter's place in the map. Other marks — a Devanagari vowel sign — keep a place of
 * their own, so "मेर" still finds the start of "मेरा". Every run of white space becomes one space, and a leading run
 * is dropped. Walks code points, so a character outside the Basic Multilingual Plane is never split.
 */
export function foldText(text: string): FoldedText {
  let folded = '';
  const start: number[] = [];
  const end: number[] = [];
  let at = 0;
  while (at < text.length) {
    const first = String.fromCodePoint(text.codePointAt(at) ?? 0);
    let next = at + first.length;
    if (WHITE_SPACE.test(first)) {
      while (next < text.length && WHITE_SPACE.test(text.charAt(next))) next += 1;
      if (folded !== '') {
        folded += ' ';
        start.push(at);
        end.push(next);
      }
      at = next;
      continue;
    }
    while (next < text.length) {
      const codePoint = text.codePointAt(next) ?? 0;
      if (!isAccent(codePoint)) break;
      next += codePoint > 0xffff ? 2 : 1;
    }
    const piece = foldCluster(text.slice(at, next));
    folded += piece;
    for (let unit = 0; unit < piece.length; unit += 1) {
      start.push(at);
      end.push(next);
    }
    at = next;
  }
  return { text: folded, start, end };
}

/**
 * What a query searches for, folded: each word, or a phrase in quotes as one term (“…”, „…“ and « … » count).
 * A stray quote — an unclosed phrase — is dropped from its word. Repeats are dropped; order is kept.
 */
export function transcriptSearchTerms(query: string): string[] {
  const folded = foldText(normaliseTranscriptQuery(query)).text;
  const terms: string[] = [];
  for (const match of folded.matchAll(/"([^"]*)"|(\S+)/gu)) {
    const term = (match[1] ?? match[2] ?? '').replace(/"/gu, '').trim();
    if (term !== '' && !terms.includes(term)) terms.push(term);
  }
  return terms;
}

// --- Matching ------------------------------------------------------------------------------------

/** A stretch of a segment's original text to mark, `[start, end)` in UTF-16 units. */
export type TextRange = readonly [number, number];

/**
 * Where `terms` occur in `text`, or null when any one of them does not — every term must appear. The ranges are
 * in the original text, in order, with overlapping or touching ones merged into one. An occurrence must begin and
 * end on whole characters of the original: "1" is not found in "½" (folded "1⁄2"), nor "s" in "ß" (folded "ss").
 */
export function matchTerms(text: string, terms: readonly string[], folded: FoldedText = foldText(text)): TextRange[] | null {
  if (terms.length === 0) return null;
  const ranges: Array<[number, number]> = [];
  const units = folded.text.length;
  for (const term of terms) {
    let found = false;
    for (let at = folded.text.indexOf(term); at !== -1; ) {
      const last = at + term.length - 1;
      const whole = (at === 0 || folded.start[at - 1] !== folded.start[at]) && (last === units - 1 || folded.start[last + 1] !== folded.start[last]);
      if (whole) {
        found = true;
        ranges.push([folded.start[at] ?? 0, folded.end[last] ?? text.length]);
      }
      at = folded.text.indexOf(term, whole ? last + 1 : at + 1);
    }
    if (!found) return null;
  }
  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: Array<[number, number]> = [];
  for (const [rangeStart, rangeEnd] of ranges) {
    const previous = merged[merged.length - 1];
    if (previous && rangeStart <= previous[1]) previous[1] = Math.max(previous[1], rangeEnd);
    else merged.push([rangeStart, rangeEnd]);
  }
  return merged;
}

/** `text` cut into the stretches to mark and those between them, in order; empty stretches are left out. */
export function highlightParts(text: string, ranges: readonly TextRange[]): Array<{ text: string; match: boolean }> {
  const parts: Array<{ text: string; match: boolean }> = [];
  let at = 0;
  for (const [rangeStart, rangeEnd] of ranges) {
    if (rangeStart > at) parts.push({ text: text.slice(at, rangeStart), match: false });
    if (rangeEnd > rangeStart) parts.push({ text: text.slice(rangeStart, rangeEnd), match: true });
    at = Math.max(at, rangeEnd);
  }
  if (at < text.length) parts.push({ text: text.slice(at), match: false });
  return parts;
}

export interface TranscriptHit {
  segment: TranscriptSegment;
  ranges: TextRange[];
}

/**
 * The segments whose text holds every term, in the order spoken, each with what to mark. `foldedTexts` is the
 * segments' folded text, index for index, folded once per transcript rather than once per keystroke; the map back
 * to the original is worked out only for the segments that match.
 */
export function searchTranscript(segments: readonly TranscriptSegment[], foldedTexts: readonly string[], terms: readonly string[]): TranscriptHit[] {
  if (terms.length === 0) return [];
  const hits: TranscriptHit[] = [];
  segments.forEach((segment, position) => {
    const folded = foldedTexts[position] ?? '';
    if (!terms.every((term) => folded.includes(term))) return;
    const ranges = matchTerms(segment.text, terms);
    if (ranges) hits.push({ segment, ranges });
  });
  return hits;
}

// --- Links into the transcript, named whole (05-content §8; the Phase 1c audit's D30) ------------------------------

/**
 * A segment's time link's accessible name: "Segment 4 at 03:15", and "…, in the full transcript" where it leaves the
 * search's results. Why four whole templates: the name was "Segment N" with " at mm:ss" spliced on by condition.
 */
export function segmentLinkLabel(index: number, startSeconds: number | null, inFullTranscript: boolean): string {
  const number = index + 1;
  if (startSeconds === null) return inFullTranscript ? `Segment ${number}, in the full transcript` : `Segment ${number}`;
  const at = formatTimestamp(startSeconds);
  return inFullTranscript ? `Segment ${number} at ${at}, in the full transcript` : `Segment ${number} at ${at}`;
}

/** A to-do's quote's link: "Go to transcript at 03:15", or "Go to transcript" where no moment was kept — each whole. */
export function transcriptLinkText(offsetSeconds: number | null): string {
  return offsetSeconds === null ? 'Go to transcript' : `Go to transcript at ${formatTimestamp(offsetSeconds)}`;
}
